import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  EditableCallbackParams,
  ExcelCell,
  GridApi,
  HeaderClassParams,
  ProcessCellForExportParams,
  SuppressKeyboardEventParams,
} from '@ag-grid-community/core';
import { isArray, isBoolean } from 'lodash-es';
import { BehaviorSubject, isObservable } from 'rxjs';

import { TableConstants } from '@shared/constants/table.constants';
import { CurrencyToggle } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { ActivityType } from './gql.service';

export type ColumnsWithEditClassesAndExpandCollapseFunction<T> = (
  getClassNames: EditClassNamesFunction,
  showUnitTotals$: BehaviorSubject<boolean>,
  selectedMonth: string
) => (ColDef<T> | ColGroupDef<T>)[];

export type ColumnsWithEditClassesFunction<T> = (
  getClassNames: EditClassNamesFunction,
  showUnitTotals$: BehaviorSubject<boolean>
) => (ColDef<T> | ColGroupDef<T>)[];

export type EditClassNamesFunction = (
  classes: string[]
) => (cellClassParams: CellClassParams) => string[];

export interface FilterEntity {
  type: 'date' | 'text';
  value: string | string[];
}

export type GetEditClassHeaderNames = GetEditClass<HeaderClassParams>;

export type GetEditClassNames = GetEditClass<CellClassParams>;

export type SelectedVendorColumnEditClassesFunction<T> = (
  getClassNames: EditClassNamesFunction,
  editMode$: BehaviorSubject<boolean>,
  showUnitTotals$: BehaviorSubject<boolean>,
  hideVendorEstimatePercentLTD$: BehaviorSubject<boolean>,
  hideVendorEstimateAmountLTD$: BehaviorSubject<boolean>,
  hasEditVendorEstimatePermission$: BehaviorSubject<boolean>,
  isVendorEstimateLocked$: BehaviorSubject<boolean>
) => (ColDef<T> | ColGroupDef<T>)[];

export interface TableFilter {
  dateFrom?: string;
  filter?: string;
  filterType?: 'date' | 'set' | 'text';
  type?: 'equals' | 'greaterThanOrEqual' | 'inRange' | 'lessThan';
  values?: string[];
}

type GetEditClass<T> = (
  editModeActivated$: BehaviorSubject<boolean> | boolean
) => (cellClasses: string[]) => (cellClassParams: T) => string[];

type GetTotalRowArgs = {
  aggregateColumns: string[];
  aggregateColumnsPatterns?: string[];
  amountColumnKey?: string;
  excludeColumns: string[];
  gridApi: GridApi;
  numberColumnKey?: string;
  totalData: Record<string, number>;
  useContractCurrency?: boolean;
};

export class TableService {
  static clearCellRange = (
    params: SuppressKeyboardEventParams,
    cb?: (e: SuppressKeyboardEventParams, startRowIndex: number, endRowIndex: number) => void,
    skipCell?: (colId: string, dData: Record<string, unknown>) => boolean
  ) => {
    params.api.getCellRanges()?.forEach((range) => {
      const colIds = range.columns
        .filter((col) => !!col.getColDef().editable)
        .map((col) => col.getColId());

      const rangeStartRowIndex = range?.startRow?.rowIndex || 0;
      const rangeEndRowIndex = range?.endRow?.rowIndex || 0;

      const startRowIndex = Math.min(rangeStartRowIndex, rangeEndRowIndex);

      const endRowIndex = Math.max(rangeStartRowIndex, rangeEndRowIndex);

      this.clearCells(startRowIndex, endRowIndex, colIds, params.api, skipCell);

      if (cb && colIds.length) {
        cb(params, startRowIndex, endRowIndex);
      }
    });
  };

  static clearCells(
    start: number,
    end: number,
    columns: string[],
    gridApi: GridApi,
    skipCell?: (colId: string, dData: Record<string, unknown>) => boolean
  ) {
    const itemsToUpdate = [];

    for (let i = start; i <= end; i++) {
      const dData = gridApi.getModel().getRow(i)?.data;

      if (dData) {
        columns.forEach((column: number | string) => {
          // Skip this cell if skipCell callback returns true
          if (skipCell && skipCell(column.toString(), dData)) {
            return;
          }

          if (column.toString().includes('.')) {
            const [key, col] = column.toString().split('.');

            dData[key][col] = 0;
          } else {
            dData[column] = 0;
          }
        });

        itemsToUpdate.push(dData);
      }
    }

    gridApi.applyTransaction({ update: itemsToUpdate });
  }

  static generateTotalRow = (rows: Record<string, number>[], ignoreColsForTotal: string[]) => {
    return rows.reduce((accum, value) => {
      const result = { ...accum };

      Object.entries(value).forEach(([key, val]) => {
        if (ignoreColsForTotal.indexOf(key) === -1 && !value.patient_protocol_optional) {
          result[key] = (accum[key] || 0) + (Utils.isNumber(val) ? val : 0);
        }
      });

      return result;
    }, {});
  };

  static isEditableCell =
    (editModeActivated: BehaviorSubject<boolean> | boolean, locked = new BehaviorSubject(false)) =>
    (cell: CellClassParams | EditableCallbackParams): boolean => {
      if (
        cell.node.group ||
        (cell.node.data.activity_type === ActivityType.ACTIVITY_DISCOUNT &&
          cell.colDef.field !== 'vendor_estimate_amount' &&
          cell.colDef.field !== 'vendor_estimate_amount_ltd') ||
        cell.node?.isRowPinned() ||
        locked.value
      ) {
        return false;
      }

      return isBoolean(editModeActivated) ? editModeActivated : editModeActivated.getValue();
    };

  static getEditableCellClasses: GetEditClassNames =
    (editModeActivated) => (cellClasses) => (params: CellClassParams) => {
      const editMode = isObservable(editModeActivated)
        ? editModeActivated.getValue()
        : editModeActivated;

      if (!editMode || params.node.isRowPinned()) {
        return cellClasses;
      }

      return TableService.isEditableCell(editMode)(params)
        ? [...cellClasses, TableConstants.STYLE_CLASSES.EDITABLE_CELL]
        : [...cellClasses, TableConstants.STYLE_CLASSES.NOT_EDITABLE_CELL];
    };

  static getEditableHeaderClasses: GetEditClassHeaderNames =
    (editModeActivated) => (cellClasses) => () => {
      const editMode = isObservable(editModeActivated)
        ? editModeActivated.getValue()
        : editModeActivated;

      return editMode
        ? [...cellClasses, TableConstants.STYLE_CLASSES.EDITABLE_HEADER]
        : [...cellClasses, TableConstants.STYLE_CLASSES.NOT_EDITABLE_HEADER];
    };

  static getNonEditableCellClasses: GetEditClassNames =
    (editModeActivated) =>
    (cellClasses) =>
    (cellClassParams): string[] => {
      const isRowPinned = !!cellClassParams?.node?.isRowPinned();
      const editMode = isObservable(editModeActivated)
        ? editModeActivated.getValue()
        : editModeActivated;

      return editMode && !isRowPinned
        ? [...cellClasses, TableConstants.STYLE_CLASSES.NOT_EDITABLE_CELL]
        : cellClasses;
    };

  static getServerSideFilters(filters: Record<string, FilterEntity>): Record<string, TableFilter> {
    const parseTextType = (filterValue: string | string[]) => {
      const isInvalidValue = isArray(filterValue)
        ? !filterValue.filter(Boolean).length
        : !filterValue?.length;

      if (isInvalidValue) {
        return null;
      }

      if (isArray(filterValue) && filterValue.length === 1) {
        return { filter: filterValue[0], filterType: 'text', type: 'equals' };
      }

      return isArray(filterValue)
        ? { filterType: 'set', values: filterValue }
        : { filter: filterValue, filterType: 'text', type: 'equals' };
    };

    const parseDateType = (filterValue: string[]) => {
      const [fromDate, toDate] = filterValue;

      if (!fromDate && !toDate) {
        return null;
      }

      if (!fromDate && toDate) {
        return { dateFrom: toDate, filterType: 'date', type: 'lessThan' };
      }

      if (fromDate && !toDate) {
        return { dateFrom: fromDate, filterType: 'date', type: 'greaterThanOrEqual' };
      }

      return {
        dateFrom: fromDate,
        dateTo: toDate,
        filterType: 'date',
        type: 'inRange',
      };
    };

    return Object.entries(filters).reduce((accum, [key, filter]) => {
      let filterObj = null;

      if (filter.type === 'text') {
        filterObj = parseTextType(filter.value);
      }

      if (filter.type === 'date') {
        filterObj = parseDateType(filter.value as string[]);
      }

      if (!filterObj) {
        return accum;
      }

      return {
        ...accum,
        [key]: filterObj,
      };
    }, {});
  }

  static getTotalRowForExcel = ({
    aggregateColumns,
    aggregateColumnsPatterns = [],
    amountColumnKey = '::count',
    excludeColumns,
    gridApi,
    numberColumnKey = '::costs',
    totalData,
    useContractCurrency = false,
  }: GetTotalRowArgs) => {
    const totalRow = gridApi
      .getAllDisplayedColumns()
      .filter((key) => excludeColumns.indexOf(key.getColId()) === -1)
      .reduce<Record<string, null | number>>(
        (accum, col) => ({
          ...accum,
          [col.getColId()]: null,
        }),
        {}
      );

    Object.keys(totalRow).forEach((key) => {
      if (
        aggregateColumns.indexOf(key) !== -1 ||
        aggregateColumnsPatterns.some((field) => key.includes(field))
      ) {
        if (useContractCurrency) {
          const total = totalData[`${key}Contracted`] || totalData[`${key}::contracted`] || 0;

          totalRow[key] = total;
        } else {
          totalRow[key] = totalData[key];
        }
      }
    });

    return Object.entries(totalRow).reduce<ExcelCell[]>((accum, [key, value]) => {
      const totalValue = value || 0;

      if (aggregateColumns.indexOf(key) !== -1) {
        accum.push({
          data: { type: 'Number', value: `${totalValue}` },
          styleId: key.endsWith(amountColumnKey) ? 'total_row_amount' : 'total_row',
        });
      } else {
        accum.push({
          data: { type: 'String', value: (value || Utils.zeroHyphen) + '' },
          styleId: key.endsWith(numberColumnKey) ? 'total_row' : 'total_text_format',
        });
      }

      return accum;
    }, []);
  };

  static isEditableCellWithExtraPermission =
    (
      editModeActivated: BehaviorSubject<boolean> | boolean,
      extraPermission: BehaviorSubject<boolean> | boolean
    ) =>
    (cell: CellClassParams | EditableCallbackParams): boolean => {
      if (isBoolean(extraPermission)) {
        if (!extraPermission) {
          return false;
        }
      } else {
        if (extraPermission.getValue() === false) {
          return false;
        }
      }

      if (
        cell.node.group ||
        (cell.node.data.activity_type === ActivityType.ACTIVITY_DISCOUNT &&
          cell.colDef.field !== 'vendor_estimate_amount' &&
          cell.colDef.field !== 'vendor_estimate_amount_ltd') ||
        cell.node?.isRowPinned()
      ) {
        return false;
      }

      return isBoolean(editModeActivated) ? editModeActivated : editModeActivated.getValue();
    };

  static processCellForExcel =
    (
      selectedCurrency: BehaviorSubject<CurrencyToggle>,
      currencyColNames: string[],
      costKeyName?: string,
      expandCurrency = false
    ) =>
    (params: ProcessCellForExportParams): string => {
      const colId = params.column.getColId();
      const isCurrencyCell =
        (costKeyName ? colId.endsWith(costKeyName) : false) ||
        currencyColNames.indexOf(colId) !== -1;

      return isCurrencyCell
        ? Utils.agMultipleCurrencyFormatter(
            selectedCurrency,
            expandCurrency
          )({
            data: params.node?.data,
            value: params.value,
          })
        : params.value || Utils.zeroHyphen;
    };
}
